'use client';
import { ArrowLeftIcon } from '@radix-ui/react-icons';
import termsBG from '../assets/images/terms-and-conditions.webp';
import finalLogo from '../assets/images/logo-white.svg';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import { useState, useEffect } from 'react';

const styles = {
	bgImage: 'max-h-[80vh] w-full bg-cover bg-center bg-no-repeat relative',
	arrowLeftIcon: 'w-full h-full text-white hover:text-gray transition-colors',
	finalLogo: 'absolute top-[100px] w-[260px] h-[48px] left-1/2 -translate-x-1/2',
	mainHeading:
		'text-white text-[48px] sm:text-[80px] xl:text-[120px] font-bold leading-1.2 lg:leading-1.7',
	container: 'flex-1 gap-[60px] px-6 md:px-[60px] lg:px-[120px] pt-[60px] pb-0',
	containerHeading:
		'mb-6 text-2xl sm:text-2.5xl lg:text-40 font-semibold leading-170 tracking-0 text-dark-blue',
	para: 'text-gray space-y-4 text-sm sm:text-15 lg:text-base xl:text-lg font-semibold leading-170 tracking-0',
	subHeading: 'text-dark-blue text-lg sm:text-xl lg:text-2xl font-semibold mt-6 mb-3',
	sectionList: 'text-gray text-sm sm:text-15 lg:text-base xl:text-lg font-semibold leading-170 tracking-0 ml-4 space-y-2',
	listItem: 'list-disc ml-4',
};

const PrivacyPolicyPage = ({ onBack }) => {
	const [isImageLoaded, setIsImageLoaded] = useState(false);

	useEffect(() => {
		const img = new Image();
		img.src = termsBG;
		img.onload = () => setIsImageLoaded(true);
	}, []);

	return (
		<div
			className={styles.bgImage}
			style={isImageLoaded ? { backgroundImage: `url(${termsBG})` } : {}}
		>
			{!isImageLoaded && (
				<div className="absolute inset-0">
					<Skeleton
						height="100%"
						width="100%"
						baseColor="#E5E7EB"
						highlightColor="#F3F4F6"
						duration={1.2}
					/>
				</div>
			)}
			{/* Dark overlay */}
			<div className="absolute inset-0 bg-black/50"></div>

			<div className="relative z-10 flex flex-col min-h-screen">
				{/* Header */}
				<div className="flex items-center justify-between">
					<button
						onClick={onBack}
						className="
              absolute
              top-[20px] left-[20px]      /* Mobile */
              sm:top-[30px] sm:left-[40px] /* Small tablets */
              md:top-[40px] md:left-[60px] /* Medium tablets */
              lg:top-[50px] lg:left-[80px] /* Laptops */
              xl:top-[60px] xl:left-[100px] /* Large screens */
              w-[40px] h-[40px] 
              sm:w-[50px] sm:h-[50px]
              xl:w-[60px] xl:h-[60px]
            "
					>
						<ArrowLeftIcon className={styles.arrowLeftIcon} />
					</button>

					<img
						src={finalLogo}
						alt="WriteForm"
						className={styles.finalLogo}
					/>

					{/* Spacer */}
					<div className="w-6"></div>
				</div>

				{/* Main heading */}
				<div className="text-center px-6 mt-[200px] md:mt-[220px]">
					<h1 className={styles.mainHeading}>
						Privacy Policy
					</h1>
				</div>

				{/* Content container */}
				<div className={styles.container}>
					<div className="bg-white rounded-[50px] p-6 md:p-8 lg:p-12 min-h-[60vh] pb-0">
						<h2 className={styles.containerHeading}>
							<div>
								<span className="font-bold">Effective Date:</span>{' '}
								<span className="font-medium">August 15, 2025</span>
							</div>
							<div className="mt-1">
								<span className="font-bold">Last Updated:</span>{' '}
								<span className="font-medium">December 19, 2025</span>
							</div>
						</h2>

						<div className={styles.para}>
							<p>
								This Privacy Policy ("Policy") describes how WriteForm LLC ("WriteForm," "Company," "we," "our," or "us") collects, uses, stores, and protects personal information in connection with the WriteForm platform, website, and related services (collectively, the "Services"). By accessing or using the Services, you acknowledge that you have read and understood this Policy. If you do not agree, you must discontinue use of the Services.
							</p>

							<h3 className={styles.subHeading}>1. Information We Collect</h3>
							<p>
								We collect only the personal information necessary to provide and improve the Services, including:
							</p>
							<ul className={styles.sectionList}>
								<li className={styles.listItem}>First name, last name, and email address provided during account creation</li>
								<li className={styles.listItem}>Account credentials and authentication data</li>
								<li className={styles.listItem}>User-generated documents and related metadata</li>
								<li className={styles.listItem}>Document status indicators (e.g., Draft, Review, Final)</li>
								<li className={styles.listItem}>
									Technical and usage information collected automatically, such as IP address, browser type, device information, and general usage patterns
								</li>
							</ul>
							<p>
								We do not collect or store full payment card numbers, CVV codes, or bank account details.
							</p>

							<h3 className={styles.subHeading}>2. How We Use Information</h3>
							<p>We use collected information to:</p>
							<ul className={styles.sectionList}>
								<li className={styles.listItem}>Provide, operate, and maintain the Services</li>
								<li className={styles.listItem}>Enable secure account access and authentication</li>
								<li className={styles.listItem}>
									Process payments through a secure, PCI-compliant third-party payment processor
								</li>
								<li className={styles.listItem}>Store, process, and export user documents</li>
								<li className={styles.listItem}>Apply formatting logic, including watermarks based on subscription tier</li>
								<li className={styles.listItem}>Monitor for misuse, fraud, or violations of our Terms of Service</li>
								<li className={styles.listItem}>Communicate with users about account-related matters</li>
								<li className={styles.listItem}>Comply with applicable legal obligations</li>
							</ul>
							<p>
								Billing, subscription management, cancellations, refunds, and plan changes are governed by the WriteForm Terms of Service.
							</p>

							<h3 className={styles.subHeading}>3. User Documents and Content</h3>
							<p>
								Users retain ownership of all documents and content uploaded or created using the Services. WriteForm does not claim ownership or authorship of user documents. WriteForm processes and stores documents solely to provide the Services and does not use user documents to train artificial intelligence or machine learning models. Certain exported documents may include visible watermarks on every page based on the user's subscription plan.
							</p>

							<h3 className={styles.subHeading}>4. Cookies and Tracking Technologies</h3>
							<p>
								WriteForm uses cookies and similar technologies to improve site performance, enhance user experience, and analyze usage trends.
							</p>
							<p>We use the following categories of cookies:</p>
							<ul className={styles.sectionList}>
								<li className={styles.listItem}>
									<span className="font-bold">Strictly necessary cookies</span> for secure login and platform operation
								</li>
								<li className={styles.listItem}>
									<span className="font-bold">Performance and analytics cookies</span> to collect aggregated, non-identifiable usage data
								</li>
							</ul>
							<p>
								We do not use cookies for advertising or behavioral tracking. Users may disable cookies through browser settings, though doing so may limit functionality.
							</p>

							<h3 className={styles.subHeading}>5. Data Storage and Retention</h3>
							<p>
								User data and documents are stored using secure cloud infrastructure and may be processed in the United States or other jurisdictions where WriteForm or its service providers operate. Documents may be retained for up to one hundred twenty (120) days after account cancellation, termination, or inactivity, after which they may be archived or permanently deleted. Backup copies may persist temporarily for disaster recovery purposes. Users are responsible for maintaining local backups of their work.
							</p>

							<h3 className={styles.subHeading}>6. Security Measures</h3>
							<p>
								WriteForm employs commercially reasonable administrative, technical, and organizational safeguards to protect personal information and stored documents. However, no system is completely secure. Users are solely responsible for safeguarding their login credentials, and WriteForm shall not be liable for unauthorized access resulting from a user's failure to maintain adequate security practices.
							</p>

							<h3 className={styles.subHeading}>7. Third-Party Services</h3>
							<p>
								We use third-party service providers for payment processing, email delivery, analytics, and infrastructure hosting. These providers are contractually obligated to handle data securely and only for authorized purposes. The Services may contain links to third-party websites, and WriteForm is not responsible for the privacy practices or content of those sites.
							</p>

							<h3 className={styles.subHeading}>8. User Rights (GDPR and Similar Laws)</h3>
							<p>
								For users located in the European Union or European Economic Area, personal data is processed in accordance with the General Data Protection Regulation (GDPR). Users may request:
							</p>
							<ul className={styles.sectionList}>
								<li className={styles.listItem}>Access to their personal data</li>
								<li className={styles.listItem}>Correction of inaccurate data</li>
								<li className={styles.listItem}>Deletion of personal data</li>
								<li className={styles.listItem}>Restriction or objection to processing</li>
								<li className={styles.listItem}>Data portability</li>
							</ul>
							<p>
								Requests may be submitted to <span className="font-semibold">privacy@writeformapp.com</span>.
							</p>

							<h3 className={styles.subHeading}>9. Academic and Institutional Disclaimer</h3>
							<p>
								WriteForm provides formatting and citation assistance tools only. We do not guarantee originality, plagiarism detection, academic approval, or compliance with institutional policies. WriteForm is not affiliated with or endorsed by the American Psychological Association or any academic institution. All formatting logic is derived from publicly available educational resources.
							</p>

							<h3 className={styles.subHeading}>10. Changes to This Policy</h3>
							<p>
								WriteForm may update this Privacy Policy from time to time. Changes will be effective immediately upon posting, and the "Last Updated" date will reflect the revision. Continued use of the Services constitutes acceptance of the revised Policy.
							</p>

							<h3 className={styles.subHeading}>11. Contact Information</h3>
							<p>
								Questions or concerns regarding this Privacy Policy may be directed to: <span className="font-bold">privacy@writeformapp.com</span>.
							</p>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
};

export default PrivacyPolicyPage;